import React from 'react';
import { IMG, SpeakCta, ASSESSMENT_URL, DEMO_URL } from './shared';

/** Platforms the host quotes in the hero image were gathered from. */
const SOURCES = [
  { name: 'Reddit', logo: `${IMG}/brand-logos/reddit.svg` },
  { name: 'Facebook', logo: `${IMG}/brand-logos/facebook.svg` },
  { name: 'Instagram', logo: `${IMG}/brand-logos/instagram.svg` },
];

const BADGES = ['Banned from Turo', 'Turo Alternatives'];

/** Host phrases in the order a deactivated host actually moves through them:
 *  shock and frustration first, then resolve and the upside of going direct.
 *  Paraphrased from public posts, not attributed to anyone. */
const QUOTES = [
  'Deactivated, no warning.',
  'No one will tell me why.',
  'Still making payments.',
  "I'll rent them myself.",
  "I'm ready to start my direct rentals.",
  'My cars, my rules, my renters.',
  'They book me directly now.',
  'Direct rentals, better margins.',
];

/** The single spot the animation plays in — clear sky to the left of the
 *  raised tailgate, matching the approved placement. */
const QUOTE_SPOT = { left: 8, top: 15, depth: 1.2 };

const STAGGER_S = 2.6;
const CYCLE_S = QUOTES.length * STAGGER_S;

const HeroQuotes = () => (
  <div className="mpa-hero-quotes" style={{ containerType: 'size' }} aria-hidden="true">
    {QUOTES.map((quote, i) => (
      <p
        key={quote}
        data-lead={i === 0 ? 'true' : undefined}
        className="mpa-quote-line"
        style={{
          left: `${QUOTE_SPOT.left}%`,
          top: `${QUOTE_SPOT.top}%`,
          maxWidth: `${54 - QUOTE_SPOT.left}cqw`,
          fontSize: `max(13px, calc(clamp(13px, 3.7cqw, 23px) * ${QUOTE_SPOT.depth}))`,
          '--drop': `calc(13px * ${QUOTE_SPOT.depth})`,
          '--cycle': `${CYCLE_S}s`,
          '--delay': `${i * STAGGER_S - CYCLE_S}s`,
        }}
      >
        {`“${quote}”`}
      </p>
    ))}
  </div>
);

/** SEO-focused hero: Turo alternative, private & direct rentals keywords in
 *  the h1, kicker, and body. */
const HeroSection = () => (
  <section className="mpa-hero">
    <div className="mpa-container mpa-hero-inner">
      <div className="mpa-hero-grid">
        {/* Copy */}
        <div className="mpa-hero-copy">
          <div className="mpa-hero-badges">
            {BADGES.map((badge) => (
              <span key={badge} className="mpa-badge">
                {badge}
              </span>
            ))}
          </div>

          <h1 className="mpa-hero-title">The Turo alternative for private &amp; direct rentals.</h1>

          <p className="mpa-hero-tag">Build your own rental business, not another marketplace profile.</p>

          <p className="mpa-hero-sub">
            Rentid gives you the booking, insurance, renter verification, payments, and rental
            management infrastructure to go direct and stay in control.
          </p>

          <p className="mpa-hero-note">Direct rental platform built specifically for your business.</p>

          <div className="mpa-hero-ctas">
            <SpeakCta href={DEMO_URL} label="Book Demo" />
            <SpeakCta href={ASSESSMENT_URL} label="Speak with an Expert" tone="dark" />
          </div>

          <p className="mpa-hero-services">
            Direct rental software &bull; On Rent insurance &bull; Off Rent insurance &bull; Renter
            verification &bull; Fleet management
          </p>
        </div>

        {/* Image */}
        <div className="mpa-hero-media">
          <div className="mpa-hero-frame">
            <img
              src={`${IMG}/hero/independent-operator-hero.webp`}
              alt="An independent rental operator standing in front of their own fleet of rental vehicles, running private and direct rentals as a Turo alternative"
              className="mpa-hero-img"
              width={720}
              height={720}
            />
            <HeroQuotes />

            {/* Sourcing credit for the host quotes, on glass in the corner. */}
            <div className="mpa-hero-credit-wrap">
              <div className="mpa-hero-credit">
                <span className="mpa-hero-credit-text">Actual Turo host testimonies sourced from</span>
                <span className="mpa-hero-credit-logos">
                  {SOURCES.map((source) => (
                    <img key={source.name} src={source.logo} alt={source.name} width={16} height={16} />
                  ))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default HeroSection;
